import * as React from "react";
import { useEffect, useRef, useState, useCallback, useMemo } from "react";

import config from "../../config/config";
import authHeader from "../../config/authHeader";

import { ButtonComponent, CheckBoxComponent } from "@syncfusion/ej2-react-buttons";
import { TimePickerComponent } from "@syncfusion/ej2-react-calendars";
import {
  DropDownListComponent,
  MultiSelectComponent,
  CheckBoxSelection,
} from "@syncfusion/ej2-react-dropdowns";
import { UploaderComponent } from "@syncfusion/ej2-react-inputs";
import {
  ToolbarComponent,
  ItemsDirective,
  ItemDirective,
  ContextMenuComponent,
  AppBarComponent,
} from "@syncfusion/ej2-react-navigations";

import {
  ScheduleComponent,
  Day,
  Week,
  WorkWeek,
  Month,
  Year,
  TimelineViews,
  TimelineMonth,
  TimelineYear,
  ViewsDirective,
  ViewDirective,
  ResourcesDirective,
  ResourceDirective,
  Inject,
  Resize,
  DragAndDrop,
  Agenda,
  Print,
  ExcelExport,
  ICalendarImport,
  ICalendarExport,
} from "@syncfusion/ej2-react-schedule";

import { DropDownButtonComponent } from "@syncfusion/ej2-react-splitbuttons";

import {
  addClass,
  closest,
  Internationalization,
  isNullOrUndefined,
  removeClass,
  remove,
  createElement,
  compile,
} from "@syncfusion/ej2-base";

import { Query } from "@syncfusion/ej2-data";
import { DropDownList, MultiSelect } from "@syncfusion/ej2-dropdowns";

import "../../css/syncfusion.css";

MultiSelect.Inject(CheckBoxSelection);

/**
 * IMPORTANT FIXES vs your version:
 * - args.data in Syncfusion is an OBJECT (or array). Your code wrapped it into an array then used data.StartTime => undefined.
 * - When sending FormData, DO NOT keep "Content-Type: application/json" from authHeader().
 * - Your Schedule uses React state as datasource => you MUST update localEvents on success (not only scheduleObj.addEvent).
 */

function AgendaEquipe({ sites = [], enumsNomGroupe = [], personnel = [], events = [] }) {
  const BASE_DEFAULT_VIEW = "WorkWeek";
  const [baseView, setBaseView] = useState(BASE_DEFAULT_VIEW);
  const resolvedView = useMemo(() => {
    if (baseView === "Agenda") return "Agenda";
    return "Timeline" + baseView;
  }, [baseView]);
  const [scheduleReady, setScheduleReady] = useState(false);

  console.log("events =", events);
  const [slotDuration, setSlotDuration] = useState(60);

  const scheduleObj = useRef(null);
  const contextMenuObj = useRef(null);
  const timeBtn = useRef(null);
  const intl = new Internationalization();
  const workWeekObj = useRef(null);

  const readyRef = useRef(false);
  const didInitRef = useRef(false);

  const committingRef = useRef({ create: false, change: false, remove: false });

  const getSch = useCallback(() => {
    return readyRef.current ? scheduleObj.current : null;
  }, []);

  const onScheduleCreated = useCallback(() => {
    readyRef.current = true;
    setScheduleReady(true);

    if (didInitRef.current) return;
    didInitRef.current = true;

    const sch = scheduleObj.current;
    if (!sch) return;

    requestAnimationFrame(() => {
      sch.refreshLayout?.();
      sch.refreshEvents?.();
    });
  }, []);



  let selectedTarget;

  const BLANK_ID = "-1";
  const BLANK_USER = { Name: "À Programmer", personnelUUID: BLANK_ID, Color: "#9CA3AF" };

  const headersForFormData = useCallback(() => {
    const h = { ...(authHeader?.() ?? {}) };
    // do not force content-type for FormData
    delete h["Content-Type"];
    delete h["content-type"];
    return h;
  }, []);

  // -------------------- stable color helper --------------------
  const colorFromString = useCallback((str) => {
    const s = String(str ?? "").trim();
    if (!s) return "#64748b";

    let hash = 0;
    for (let i = 0; i < s.length; i++) hash = (hash * 31 + s.charCodeAt(i)) | 0;
    const hue = Math.abs(hash) % 360;
    return `hsl(${hue}, 70%, 45%)`;
  }, []);

  const users = useMemo(() => {
    const arr = Array.isArray(personnel) ? personnel : [];
    return arr
      .map((p) => {
        const id = String(p?.UUID_ ?? p?.personnelUUID ?? "");
        const name =
          [p?.Prenom, p?.Nom].filter(Boolean).join(" ").trim() ||
          String(p?.Nom ?? "").trim() ||
          "Sans nom";

        return {
          ...p,
          personnelUUID: id,
          Name: name,
          Color: p?.Color ?? p?.color ?? colorFromString(id || name),
        };
      })
      .filter((u) => u.personnelUUID);
  }, [personnel, colorFromString]);

  const rawGroups = Array.isArray(enumsNomGroupe) ? enumsNomGroupe : [];
  const groupOptionsBase = ["— Aucun groupe —", ...rawGroups];

  // your datasource state
  const [localEvents, setLocalEvents] = useState(() => (Array.isArray(events) ? events : []));
  useEffect(() => {
    setLocalEvents(Array.isArray(events) ? events : []);
  }, [events]);

  const onEventRendered = useCallback(
    (args) => {
      if (!args?.element) return;
      const gName = String(args.data?.groupeName ?? args.data?.groupeId ?? "");
      if (!gName) return;

      const c = colorFromString(gName);
      args.element.style.backgroundColor = c;
      args.element.style.borderColor = c;
    },
    [colorFromString]
  );

  const normalizedEvents = useMemo(() => {
    return (localEvents ?? []).map((e) => ({
      ...e,
      Id: String(e.Id ?? e.uuid ?? ""),
      Subject: String(e.Subject ?? e.titre ?? ""),
      StartTime: e.StartTime,
      EndTime: e.EndTime,
      personnelUUID: Array.isArray(e.personnelUUID)
        ? e.personnelUUID.map(String)
        : e.personnelUUID
          ? [String(e.personnelUUID)]
          : [BLANK_ID],
      groupeName: String(e.groupeName ?? e.groupeNom ?? e.groupeId ?? ""),
      siteUUID: String(e.siteUUID ?? ""),
    }));
  }, [localEvents]);


  const schedulerUsers = useMemo(() => {
    const base = (users ?? []).map((u) => ({
      ...u,
      personnelUUID: String(u.personnelUUID),
      Name: String(u.Name ?? "Sans nom"),
      Color: String(u.Color ?? "#64748b"),
    }));

    base.sort((a, b) =>
      (a.Name ?? "").localeCompare((b.Name ?? ""), "fr", { sensitivity: "base" })
    );

    return [BLANK_USER, ...base];
  }, [users]);

  const ResourceHeader = (props) => {
    const name = props.resourceData?.Name ?? "";
    const color = props.resourceData?.Color ?? "#999";
    const initials = name
      .split(" ")
      .filter(Boolean)
      .map((s) => s[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();

    return (
      <div className="flex items-center gap-2 py-1">
        <div
          style={{ background: color }}
          className="flex justify-center items-center rounded-full w-8 h-8 font-semibold text-white text-sm"
        >
          {initials}
        </div>
        <div className="leading-tight">
          <div className="font-medium">{name}</div>
        </div>
      </div>
    );
  };

  const weekDays = [
    { text: "Lundi", value: 1 },
    { text: "Mardi", value: 2 },
    { text: "Mercredi", value: 3 },
    { text: "Jeudi", value: 4 },
    { text: "Vendredi", value: 5 },
    { text: "Samedi", value: 6 },
    { text: "Dimanche", value: 7 },
  ];

  const exportItems = [
    { text: "iCalendar", iconCss: "e-icons e-export" },
    { text: "Excel", iconCss: "e-icons e-export-excel" },
  ];

  const contextMenuItems = [
    { text: "Nouvel événement", iconCss: "e-icons e-plus", id: "Add" },
    { text: "Aujourd'hui", iconCss: "e-icons e-timeline-today", id: "Today" },
    { text: "Modifier", iconCss: "e-icons e-edit", id: "Save" },
    { text: "Supprimer", iconCss: "e-icons e-trash", id: "Delete" },
  ];

  const majorSlotData = [
    { Name: "1 heure", Value: 60 },
    { Name: "1h30", Value: 90 },
    { Name: "2 heures", Value: 120 },
    { Name: "2h30", Value: 150 },
  ];

  const importTemplateFn = (data) => {
    const template =
      '<div class="e-template-btn"><span class="e-icon-left e-btn-icon e-icons e-upload-1"></span>${text}</div>';
    return compile(template.trim())(data);
  };

  const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);

  const updateLiveTime = () => {
    const sch = scheduleObj.current;
    const scheduleTimezone = sch?.timezone || "Europe/Paris";
    const now2 = new Date();

    const dateParts = now2
      .toLocaleDateString("fr-FR", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
        timeZone: scheduleTimezone,
      })
      .split(" ");

    const liveDate = [
      capitalize(dateParts[0]),
      dateParts[1],
      capitalize(dateParts[2]),
      dateParts[3],
    ].join(" ");

    const liveTime = now2.toLocaleTimeString("fr-FR", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      timeZone: scheduleTimezone,
    });

    if (timeBtn.current) timeBtn.current.innerHTML = `${liveDate} - ${liveTime}`;
  };

  useEffect(() => {
    const id = setInterval(updateLiveTime, 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const sch = getSch();
    if (!sch) return;
    sch.currentView = resolvedView;
  }, [resolvedView, getSch]);

  useEffect(() => {
    if (!scheduleReady) return;

    const sch = scheduleObj.current;
    if (!sch) return;

    requestAnimationFrame(() => {
      sch.eventSettings = sch.eventSettings || {};
      sch.eventSettings.dataSource = normalizedEvents;

      sch.dataBind?.();
      sch.refreshEvents?.();
    });
  }, [scheduleReady, normalizedEvents, schedulerUsers.length]);



  const onPrint = () => getSch()?.print();

  const onImportClick = (args) => {
    const sch = getSch();
    if (!sch) return;
    const file = args?.event?.target?.files?.[0];
    if (file) sch.importICalendar(file);
  };

  const onExportClick = (args) => {
    const sch = getSch();
    if (!sch) return;
    if (args?.item?.text === "Excel") sch.exportToExcel({ exportType: "xlsx" });
    else sch.exportToICalendar();
  };

  const getEventData = () => {
    const sch = getSch();
    const date = sch?.selectedDate ?? new Date();
    const hour = new Date().getHours();

    return {
      Id: String(Date.now()),
      Subject: "",
      StartTime: new Date(date.getFullYear(), date.getMonth(), date.getDate(), hour, 0, 0),
      EndTime: new Date(date.getFullYear(), date.getMonth(), date.getDate(), hour + 1, 0, 0),
      Description: "",
      IsAllDay: false,
      personnelUUID: [BLANK_ID],
      groupeName: "",
      siteUUID: "",
    };
  };

  const labelToBaseView = (label) => {
    switch (label) {
      case "Jour":
        return "Day";
      case "Semaine":
        return "Week";
      case "Semaine de travail":
        return "WorkWeek";
      case "Mois":
        return "Month";
      case "Année":
        return "Year";
      case "Agenda":
        return "Agenda";
      default:
        return BASE_DEFAULT_VIEW;
    }
  };

  const onToolbarItemClicked = (args) => {
    const label = args?.item?.text;
    if (["Jour", "Semaine", "Semaine de travail", "Mois", "Année", "Agenda"].includes(label)) {
      setBaseView(labelToBaseView(label));
      return;
    }
    if (label === "Événement") {
      const sch = getSch();
      if (!sch) return;
      sch.openEditor(getEventData(), "Add", true);
    }
  };

  const isWorkCell = (el) => !!el?.classList?.contains("e-work-cells");

  const safeOpenAddEditorFromCell = (cellEl) => {
    const sch = getSch();
    if (!sch || !isWorkCell(cellEl)) return;
    const details = sch.getCellDetails([cellEl]);
    if (!details?.startTime || !details?.endTime) return;
    sch.openEditor(details, "Add");
  };

  const onCellDoubleClick = (args) => args?.element && safeOpenAddEditorFromCell(args.element);

  const onEventClick = (args) => {
    const sch = getSch();
    if (!sch) return;
    sch.openEditor(args.event, "Save");
  };

  const toDMY = (d) => {
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  };

  const pad = (n) => String(n).padStart(2, "0");
  const getLocalTime = (d) => `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;

  const onActionBegin = async (args) => {
    const sch = getSch();
    if (!sch) return;

    const data = Array.isArray(args.data) ? args.data : [args.data];

    // CREATE
    if (args.requestType === "eventCreate") {
      args.cancel = true;

      for (const item of data) {
        try {
          const fd = new FormData();
          fd.append("date_debut", toDMY(item.StartTime));
          fd.append("date_fin", toDMY(item.EndTime));
          fd.append("heure_debut", getLocalTime(item.StartTime));
          fd.append("heure_fin", getLocalTime(item.EndTime));
          fd.append("titre", String(item.Subject ?? ""));

          // IMPORTANT: FormData wants a string (send CSV)
          const pids = Array.isArray(item.personnelUUID)
            ? item.personnelUUID.map(String).join(",")
            : String(item.personnelUUID ?? BLANK_ID);
          fd.append("personnelUUID", pids);

          fd.append("siteUUID", String(item.siteUUID ?? ""));
          fd.append("groupeNom", String(item.groupeName ?? ""));

          const r = await fetch(`${config.apiBaseUrl}/4DACTION/KST_createRDVGroupe`, {
            method: "POST",
            headers: headersForFormData(),
            body: fd,
          });

          const j = await r.json();

          if (j?.status === "success") {
            const created = {
              ...item,
              Id: String(j.uuid ?? item.Id ?? Date.now()),
              Subject: String(item.Subject ?? item.titre ?? ""), // keep title
              groupeName: String(item.groupeName ?? item.groupeNom ?? ""),
              siteUUID: String(item.siteUUID ?? ""),
              personnelUUID: Array.isArray(item.personnelUUID)
                ? item.personnelUUID.map(String)
                : [String(item.personnelUUID ?? BLANK_ID)],
            };

            setLocalEvents((prev) => [...prev, created]);

          }
        } catch (e) {
          console.error("Create failed:", e);
        }
      }

      sch.closeEditor?.();
      return;
    }

    // UPDATE
    if (args.requestType === "eventChange") {
      args.cancel = true;

      const item = data[0];
      try {
        const fd = new FormData();
        fd.append("uuid", String(item.Id));
        fd.append("date_debut", toDMY(item.StartTime));
        fd.append("date_fin", toDMY(item.EndTime));
        fd.append("heure_debut", getLocalTime(item.StartTime));
        fd.append("heure_fin", getLocalTime(item.EndTime));
        fd.append("titre", String(item.Subject ?? ""));

        const pids = Array.isArray(item.personnelUUID)
          ? item.personnelUUID.map(String).join(",")
          : String(item.personnelUUID ?? BLANK_ID);
        fd.append("personnelUUID", pids);

        fd.append("siteUUID", String(item.siteUUID ?? ""));
        fd.append("groupeNom", String(item.groupeName ?? ""));

        const r = await fetch(`${config.apiBaseUrl}/4DACTION/KST_modifRDVGroupe`, {
          method: "PUT",
          headers: headersForFormData(),
          body: fd,
        });

        const j = await r.json();

        if (j?.status === "success") {
          const updated = {
            ...item,
            personnelUUID: Array.isArray(item.personnelUUID)
              ? item.personnelUUID.map(String)
              : [String(item.personnelUUID ?? BLANK_ID)],
            groupeName: String(item.groupeName ?? item.groupeId ?? ""),
            siteUUID: String(item.siteUUID ?? ""),
          };

          // ✅ React datasource update ONLY (no sch.saveEvent!)
          setLocalEvents((prev) =>
            prev.map((ev) => (String(ev.Id) === String(updated.Id) ? { ...ev, ...updated } : ev))
          );
        }
      } catch (e) {
        console.error("Update failed:", e);
      }

      sch.closeEditor?.();
      return;
    }

    // DELETE
    if (args.requestType === "eventRemove") {
      args.cancel = true;

      const item = data[0];
      try {
        const fd = new FormData();
        fd.append("uuid", String(item.Id));

        const r = await fetch(`${config.apiBaseUrl}/4DACTION/KST_deleteRDVGroupe`, {
          method: "PUT",
          headers: headersForFormData(),
          body: fd,
        });

        const j = await r.json();

        if (j?.status === "success") {
          // ✅ React datasource update ONLY (no sch.deleteEvent!)
          setLocalEvents((prev) => prev.filter((ev) => String(ev.Id) !== String(item.Id)));
        }
      } catch (e) {
        console.error("Delete failed:", e);
      }

      return;
    }
  };


  const normalizeStr = (s) =>
    (s ?? "").normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase();

  const headerSiteTemplate =
    '<div class="header flex text-xs font-semibold px-2 py-1 ml-4">' +
    '<span class="columnHeader flex-1 pr-2">Nom</span>' +
    '<span class="columnHeader flex-1 pr-2">Société</span>' +
    '<span class="columnHeader flex-1">Entité</span>' +
    "</div>";

  const itemSiteTemplate =
    '<div class="flex text-xs items-center px-2 py-1">' +
    '<span class="flex-1 pr-2 truncate text-gray-500">${Nom}</span>' +
    '<span class="flex-1 pr-2 truncate text-gray-500">${Societe}</span>' +
    '<span class="flex-1 truncate text-gray-500">${Entite}</span>' +
    "</div>";

  const onPopupOpen = (args) => {
    if (args.type !== "Editor") return;

    const formElement = args.element.querySelector(".e-schedule-form");
    if (!formElement) return;

    const startObj = args.element.querySelector(".e-start")?.ej2_instances?.[0];
    const endObj = args.element.querySelector(".e-end")?.ej2_instances?.[0];
    if (startObj && endObj) {
      startObj.format = "dd/MM/yyyy HH:mm";
      endObj.format = "dd/MM/yyyy HH:mm";
      startObj.dataBind();
      endObj.dataBind();
    }

    const data = args.data || {};
    const initialGroupName = String(data.groupeName ?? data.groupeId ?? "");
    const initialSiteUUID = data.siteUUID ? String(data.siteUUID) : "";

    const groupOptions =
      initialGroupName && !groupOptionsBase.includes(initialGroupName)
        ? [...groupOptionsBase, initialGroupName]
        : groupOptionsBase;

    const existingRow = args.element.querySelector(".custom-field-row");
    if (existingRow) {
      const hGroupe = existingRow.querySelector('input[name="groupeName"]');
      const hSite = existingRow.querySelector('input[name="siteUUID"]');

      if (hGroupe) hGroupe.value = initialGroupName;
      if (hSite) hSite.value = initialSiteUUID;

      const groupDDL = existingRow.querySelector(".ag-group-host")?.ej2_instances?.[0];
      const siteDDL = existingRow.querySelector(".ag-site-host")?.ej2_instances?.[0];

      if (groupDDL) {
        groupDDL.dataSource = groupOptions;
        groupDDL.value = initialGroupName || null;
        groupDDL.dataBind();
      }

      if (siteDDL) {
        siteDDL.dataSource = [
          { UUID_: "", Nom: "— Aucun site —", Societe: "", Entite: "" },
          ...(Array.isArray(sites) ? sites : []),
        ];
        siteDDL.value = initialSiteUUID || null;
        siteDDL.dataBind();
      }
      return;
    }

    const titleLocationRow = formElement.querySelector(".e-title-location-row");
    const row = createElement("div", { className: "custom-field-row e-row" });

    const hGroupe = createElement("input", {
      className: "e-field",
      attrs: { type: "hidden", name: "groupeName" },
    });
    const hSite = createElement("input", {
      className: "e-field",
      attrs: { type: "hidden", name: "siteUUID" },
    });

    hGroupe.value = initialGroupName;
    hSite.value = initialSiteUUID;
    row.appendChild(hGroupe);
    row.appendChild(hSite);

    const groupCol = createElement("div", { className: "e-col-12" });
    const groupHost = createElement("input", { className: "ag-group-host" });
    groupCol.appendChild(groupHost);
    row.appendChild(groupCol);

    const groupDDL = new DropDownList({
      dataSource: groupOptions,
      value: initialGroupName || null,
      floatLabelType: "Always",
      placeholder: "Groupe",
      change: (e) => {
        const v = String(e.value ?? "");
        hGroupe.value = v === "— Aucun groupe —" ? "" : v;
        hGroupe.dispatchEvent(new Event("input", { bubbles: true }));
        hGroupe.dispatchEvent(new Event("change", { bubbles: true }));
      },
    });
    groupDDL.appendTo(groupHost);

    const siteCol = createElement("div", { className: "e-col-12" });
    const siteHost = createElement("input", { className: "ag-site-host" });
    siteCol.appendChild(siteHost);
    row.appendChild(siteCol);

    const siteDDL = new DropDownList({
      dataSource: [
        { UUID_: "", Nom: "— Aucun site —", Societe: "", Entite: "" },
        ...(Array.isArray(sites) ? sites : []),
      ],
      fields: { text: "Nom", value: "UUID_" },
      value: initialSiteUUID || null,
      floatLabelType: "Always",
      placeholder: "Site",
      allowFiltering: true,
      headerTemplate: headerSiteTemplate,
      itemTemplate: itemSiteTemplate,
      filtering: (e) => {
        const q = normalizeStr(e.text);
        const all = [
          { UUID_: "", Nom: "— Aucun site —", Societe: "", Entite: "" },
          ...(Array.isArray(sites) ? sites : []),
        ];
        const filtered = all.filter((item) => normalizeStr(item.Nom).includes(q));
        e.updateData(filtered, new Query());
      },
      change: (e) => {
        hSite.value = String(e.value ?? "");
        hSite.dispatchEvent(new Event("change", { bubbles: true }));
      },
    });
    siteDDL.appendTo(siteHost);

    if (titleLocationRow?.parentNode) titleLocationRow.insertAdjacentElement("afterend", row);
    else formElement.insertBefore(row, formElement.firstChild);
  };

  const groupTemplate = useCallback(
    () => (
      <div className="template flex items-center">
        <label>
          <div className="text-child">Regroupement</div>
          <div className="icon-child">
            <CheckBoxComponent
              id="grouping"
              checked={true}
              change={(args) => {
                const sch = getSch();
                if (!sch) return;
                sch.group.resources = args.checked ? ["Users"] : [];
              }}
            />
          </div>
        </label>
      </div>
    ),
    [getSch]
  );

  const SlotDurationTemplate = useCallback(() => {
    return (
      <div className="flex flex-col items-center">
        <div className="whitespace-nowrap mr-4 text-sm">Durée du slot</div>
        <DropDownListComponent
          id="slotDuration"
          dataSource={majorSlotData}
          fields={{ text: "Name", value: "Value" }}
          value={slotDuration}
          popupHeight={150}
          change={(args) => {
            const value = args.value;
            setSlotDuration(value);

            const sch = getSch();
            if (!sch) return;

            const view = sch.currentView;
            sch.timeScale = { ...sch.timeScale, interval: value };
            sch.dataBind();
            sch.currentView = view;
          }}
          className="min-w-[120px] mt-2"
        />
      </div>
    );
  }, [slotDuration, getSch]);

  const createUpload = () => {
    const element = document.querySelector(".calendar-import .e-css.e-btn");
    element?.classList?.add("e-inherit");
  };

  const btnClick = () => {
    const settingsPanel = document.querySelector(".overview-content .right-panel");
    if (settingsPanel?.classList.contains("hide")) {
      removeClass([settingsPanel], "hide");
      workWeekObj.current?.refresh();
    } else if (settingsPanel) {
      addClass([settingsPanel], "hide");
    }
    getSch()?.refreshEvents();
  };

  const contextMenuOpen = (args) => {
    const sch = getSch();
    if (!sch || !sch.element) {
      args.cancel = true;
      return;
    }

    const newEventElement = document.querySelector(".e-new-event");
    if (newEventElement) {
      remove(newEventElement);
      removeClass([document.querySelector(".e-selected-cell")], "e-selected-cell");
    }

    sch.closeQuickInfoPopup();

    const targetElement = args.event.target;
    if (closest(targetElement, ".e-contextmenu")) return;

    selectedTarget = closest(
      targetElement,
      ".e-appointment,.e-work-cells,.e-vertical-view .e-date-header-wrap .e-all-day-cells,.e-vertical-view .e-date-header-wrap .e-header-cells"
    );

    if (isNullOrUndefined(selectedTarget)) {
      args.cancel = true;
      return;
    }

    if (selectedTarget.classList.contains("e-appointment")) {
      contextMenuObj.current?.showItems?.(["Save", "Delete"], true);
      contextMenuObj.current?.hideItems?.(["Add", "Today"], true);
      return;
    }

    if (
      selectedTarget.classList.contains("e-work-cells") &&
      !selectedTarget.classList.contains("e-selected-cell")
    ) {
      const selectedCells = sch.element.querySelectorAll(".e-selected-cell");
      removeClass([].slice.call(selectedCells), "e-selected-cell");

      selectedTarget.setAttribute("aria-selected", "true");
      selectedTarget.classList.add("e-selected-cell");
    }

    contextMenuObj.current?.hideItems?.(["Save", "Delete"], true);
    contextMenuObj.current?.showItems?.(["Add", "Today"], true);
  };

  const contextMenuSelect = (args) => {
    const sch = getSch();
    if (!sch) return;

    const selectedMenuItem = args.item.id;
    let eventObj = {};
    if (selectedTarget && selectedTarget.classList.contains("e-appointment")) {
      eventObj = sch.getEventDetails(selectedTarget);
    }

    switch (selectedMenuItem) {
      case "Today":
        sch.selectedDate = new Date();
        sch.dataBind();
        requestAnimationFrame(() => {
          sch.refresh();
          sch.refreshEvents();
        });
        break;

      case "Add": {
        const selectedCells = sch.getSelectedElements?.() ?? [];
        const inSelection = selectedCells.some((cell) => cell === selectedTarget);
        const cells = inSelection ? selectedCells : selectedTarget ? [selectedTarget] : [];
        if (!cells.length || !isWorkCell(cells[0])) break;

        const cellDetails = sch.getCellDetails(cells);
        if (!cellDetails?.startTime || !cellDetails?.endTime) break;
        sch.openEditor(cellDetails, "Add");
        break;
      }

      case "Save":
        sch.openEditor(eventObj, "Save");
        break;

      case "Delete":
        sch.deleteEvent(eventObj);
        break;

      default:
        break;
    }
  };

  return (
    <div className="schedule-control-section relative">
      <div className="col-lg-12 control-section">
        <div className="content-wrapper">
          <div className="schedule-overview">
            <AppBarComponent colorMode="Primary">
              <span className="time e-icons e-clock"></span>
              <span id="timeBtn" className="time current-time" ref={timeBtn}></span>
              <div className="e-appbar-spacer"></div>

              <div className="control-panel calendar-export">
                <ButtonComponent
                  id="printBtn"
                  cssClass="title-bar-btn e-inherit"
                  iconCss="e-icons e-print"
                  onClick={onPrint}
                  content="Imprimer"
                />
              </div>

              <div className="control-panel import-button">
                <UploaderComponent
                  id="fileUpload"
                  type="file"
                  allowedExtensions=".ics"
                  cssClass="calendar-import"
                  buttons={{ browse: importTemplateFn({ text: "Import" })[0] }}
                  multiple={false}
                  showFileList={false}
                  selected={onImportClick}
                  created={createUpload}
                />
              </div>

              <div className="control-panel calendar-export">
                <DropDownButtonComponent
                  id="exportBtn"
                  content="Exporter"
                  cssClass="e-inherit"
                  items={exportItems}
                  select={onExportClick}
                />
              </div>

              <ButtonComponent
                id="settingsBtn"
                cssClass="overview-toolbar-settings e-inherit"
                iconCss="e-icons e-settings"
                iconPosition="Top"
                content=""
                onClick={btnClick}
              />
            </AppBarComponent>

            <ToolbarComponent
              id="toolbarOptions"
              cssClass="overview-toolbar"
              width="100%"
              height={70}
              overflowMode="Scrollable"
              scrollStep={100}
              clicked={onToolbarItemClicked}
            >
              <ItemsDirective>
                <ItemDirective
                  prefixIcon="e-icons e-plus"
                  tooltipText="Nouvel événement"
                  text="Événement"
                  tabIndex={0}
                />
                <ItemDirective type="Separator" />
                <ItemDirective prefixIcon="e-icons e-day" tooltipText="Jour" text="Jour" tabIndex={0} />
                <ItemDirective
                  prefixIcon="e-icons e-week"
                  tooltipText="Semaine"
                  text="Semaine"
                  tabIndex={0}
                />
                <ItemDirective
                  prefixIcon="e-icons e-week"
                  tooltipText="Semaine de travail"
                  text="Semaine de travail"
                  tabIndex={0}
                />
                <ItemDirective prefixIcon="e-icons e-month" tooltipText="Mois" text="Mois" tabIndex={0} />
                <ItemDirective
                  prefixIcon="e-icons e-month"
                  tooltipText="Année"
                  text="Année"
                  tabIndex={0}
                />
                <ItemDirective
                  prefixIcon="e-icons e-agenda-date-range"
                  tooltipText="Agenda"
                  text="Agenda"
                  tabIndex={0}
                />
                <ItemDirective type="Separator" />
                <ItemDirective tooltipText="Regroupement" text="Regroupement" template={groupTemplate} />
                <ItemDirective
                  tooltipText="Durée du slot"
                  text="Durée du slot"
                  template={SlotDurationTemplate}
                  align="Right"
                />
              </ItemsDirective>
            </ToolbarComponent>

            <div className="overview-content">
              <div className="left-panel">
                <div className="overview-scheduler">
                  <ScheduleComponent
                    id="scheduler"
                    locale="fr"
                    timeFormat="HH:mm"
                    cssClass="schedule-overview"
                    ref={scheduleObj}
                    created={onScheduleCreated}
                    width="100%"
                    height="100%"
                    showQuickInfo={false}
                    currentView={resolvedView}
                    timezone="Europe/Paris"
                    showTimeIndicator={false}
                    eventRendered={onEventRendered}
                    popupOpen={onPopupOpen}
                    actionBegin={onActionBegin}
                    eventClick={onEventClick}
                    cellDoubleClick={onCellDoubleClick}
                    group={{ allowGroupEdit: true, resources: ["Users"], orientation: "Vertical" }}
                    eventSettings={{
                      dataSource: normalizedEvents,
                      fields: {
                        id: "Id",
                        subject: { name: "Subject", title: "Objet" },
                        startTime: { name: "StartTime", title: "Début" },
                        endTime: { name: "EndTime", title: "Fin" },
                        description: { name: "Description", title: "Description" },
                        resourceFields: "personnelUUID",
                      },
                    }}
                    resourceHeaderTemplate={ResourceHeader}
                    timeScale={{ enable: true, interval: slotDuration, slotCount: 1 }}
                  >
                    <ResourcesDirective>
                      <ResourceDirective
                        field="personnelUUID"
                        title="Utilisateur"
                        name="Users"
                        allowMultiple={true}
                        dataSource={schedulerUsers}
                        textField="Name"
                        idField="personnelUUID"
                        colorField="Color"
                      />
                    </ResourcesDirective>

                    <ViewsDirective>
                      <ViewDirective option="Day" />
                      <ViewDirective option="Week" />
                      <ViewDirective option="WorkWeek" />
                      <ViewDirective option="Month" />
                      <ViewDirective option="Year" />
                      <ViewDirective option="Agenda" />
                      <ViewDirective option="TimelineDay" />
                      <ViewDirective option="TimelineWeek" />
                      <ViewDirective option="TimelineWorkWeek" />
                      <ViewDirective option="TimelineMonth" />
                      <ViewDirective option="TimelineYear" orientation="Vertical" />
                    </ViewsDirective>

                    <Inject
                      services={[
                        Day,
                        Week,
                        WorkWeek,
                        Month,
                        Year,
                        Agenda,
                        TimelineViews,
                        TimelineMonth,
                        TimelineYear,
                        DragAndDrop,
                        Resize,
                        Print,
                        ExcelExport,
                        ICalendarImport,
                        ICalendarExport,
                      ]}
                    />
                  </ScheduleComponent>

                  <ContextMenuComponent
                    id="overviewContextMenu"
                    cssClass="schedule-context-menu"
                    ref={contextMenuObj}
                    target=".e-schedule"
                    items={contextMenuItems}
                    beforeOpen={contextMenuOpen}
                    select={contextMenuSelect}
                  />
                </div>
              </div>

              <div className="right-panel hide">
                <div className="control-panel e-css">
                  <div className="col-row">
                    <div className="col-left">
                      <label style={{ lineHeight: "34px", margin: "0" }}>
                        Premier jour de la semaine
                      </label>
                    </div>
                    <div className="col-right">
                      <DropDownListComponent
                        id="weekFirstDay"
                        dataSource={weekDays}
                        fields={{ text: "text", value: "value" }}
                        value={1}
                        popupHeight={400}
                        change={(args) => {
                          const sch = getSch();
                          if (!sch) return;
                          sch.firstDayOfWeek = args.value;
                        }}
                      />
                    </div>
                  </div>

                  <div className="col-row">
                    <div className="col-left">
                      <label style={{ lineHeight: "34px", margin: "0" }}>Semaine de travail</label>
                    </div>
                    <div className="col-right">
                      <MultiSelectComponent
                        id="workWeekDays"
                        cssClass="schedule-workweek"
                        ref={workWeekObj}
                        dataSource={weekDays}
                        mode="CheckBox"
                        fields={{ text: "text", value: "value" }}
                        enableSelectionOrder={false}
                        showClearButton={false}
                        showDropDownIcon={true}
                        value={[1, 2, 3, 4, 5]}
                        change={(args) => {
                          const sch = getSch();
                          if (!sch) return;
                          sch.workDays = args.value;
                        }}
                      >
                        <Inject services={[CheckBoxSelection]} />
                      </MultiSelectComponent>
                    </div>
                  </div>

                  <div className="col-row">
                    <div className="col-left">
                      <label style={{ lineHeight: "34px", margin: "0" }}>Heure de début de journée</label>
                    </div>
                    <div className="col-right">
                      <TimePickerComponent
                        id="dayStartHour"
                        format="HH:mm"
                        showClearButton={false}
                        value={new Date(new Date().setHours(0, 0, 0))}
                        change={(args) => {
                          const sch = getSch();
                          if (!sch) return;
                          sch.startHour = intl.formatDate(args.value, { skeleton: "Hm" });
                        }}
                      />
                    </div>
                  </div>

                  <div className="col-row">
                    <div className="col-left">
                      <label style={{ lineHeight: "34px", margin: "0" }}>Heure de fin de journée</label>
                    </div>
                    <div className="col-right">
                      <TimePickerComponent
                        id="dayEndHour"
                        format="HH:mm"
                        showClearButton={false}
                        value={new Date(new Date().setHours(23, 59, 59))}
                        change={(args) => {
                          const sch = getSch();
                          if (!sch) return;
                          sch.endHour = intl.formatDate(args.value, { skeleton: "Hm" });
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AgendaEquipe;
